import type { Event } from './events';

export type ReminderIntervalMinutes = 15 | 30 | 60 | 120 | 720 | 1440;

export type ReminderOption = Readonly<{
  /** Spelled out rather than "60m", which reads ambiguously on the button. */
  label: string;
  minutes: ReminderIntervalMinutes;
}>;

/**
 * Lead times a runner can pick from. The long ones matter for a club that meets
 * at 6am: a 12-hour reminder lands the evening before, which is when someone
 * still has time to pack a kit.
 */
export const REMINDER_OPTIONS: readonly ReminderOption[] = [
  { label: '15 minutes before', minutes: 15 },
  { label: '30 minutes before', minutes: 30 },
  { label: '1 hour before', minutes: 60 },
  { label: '2 hours before', minutes: 120 },
  { label: '12 hours before', minutes: 720 },
  { label: '1 day before', minutes: 1440 },
];

export const DEFAULT_REMINDER_MINUTES: ReminderIntervalMinutes = 60;

export type ReminderOptionState = ReminderOption &
  Readonly<{
    /** False once this lead time would land in the past for the event. */
    available: boolean;
  }>;

/**
 * Every option with a flag for whether it can still be set. The picker lists all
 * of them so a runner can see the full range, and marks the ones that have
 * already passed rather than hiding them.
 */
export function reminderOptionStates(startsAt: string, now: Date): readonly ReminderOptionState[] {
  const startsAtMs = new Date(startsAt).getTime();
  return REMINDER_OPTIONS.map((option) => ({
    ...option,
    available: startsAtMs - option.minutes * 60_000 > now.getTime(),
  }));
}

/**
 * Only the options that still land in the future. Offering "1 day before" for an
 * event starting in an hour would just fail on save.
 */
export function availableReminderOptions(startsAt: string, now: Date): readonly ReminderOption[] {
  return reminderOptionStates(startsAt, now)
    .filter((option) => option.available)
    .map(({ label, minutes }) => ({ label, minutes }));
}

export type NotificationPermissionKind =
  'unknown' | 'granted' | 'provisional' | 'denied' | 'unsupported';

export type EventPushPayload = {
  title: string;
  body: string;
  data: {
    eventId: string;
    tenantId: string;
    url: string;
  };
};

export function eventDeepLink(_tenantSlug: string, eventId: string): string {
  return `/app/events/${eventId}`;
}

export function buildEventUpdatePushPayload(
  event: Pick<Event, 'id' | 'tenant_id' | 'title' | 'starts_at'>,
  tenantSlug: string,
  timeZone?: string
): EventPushPayload {
  if (!event.tenant_id) {
    throw new Error('Personal events do not support club notifications.');
  }
  const startsAt = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    month: 'short',
    day: 'numeric',
    timeZone,
  }).format(new Date(event.starts_at));

  return {
    title: `${event.title} updated`,
    body: `Check the latest details for ${startsAt}.`,
    data: {
      eventId: event.id,
      tenantId: event.tenant_id,
      url: eventDeepLink(tenantSlug, event.id),
    },
  };
}

export function reminderDateForEvent(
  startsAt: string,
  intervalMinutes: ReminderIntervalMinutes,
  now: Date
): Date {
  const remindAt = new Date(new Date(startsAt).getTime() - intervalMinutes * 60 * 1000);
  if (remindAt <= now) {
    throw new Error('Choose a reminder before the event starts.');
  }
  return remindAt;
}

export function shouldDisableExpoPushToken(receiptStatus: string): boolean {
  return receiptStatus === 'DeviceNotRegistered';
}
